import { storeToRefs } from 'pinia';
import { useElasticStore } from '@/stores/elastic-store';
import { usePersonalizeStore } from '@/stores/personalize-store';
import { useSearch } from './useSearch';
import { useAuthStore } from '@/stores/auth-store';
import { usePersonalize } from './usePersonalize';
import dayjs from 'dayjs';
import { useRelocated } from './useRelocated';

export const useFilters = () => {
  const { addOrClearFilter } = useElasticStore();
  const { resetStore } = usePersonalizeStore();
  const { handleGetProperties } = useSearch();
  const { filters } = storeToRefs(useElasticStore());
  const { user } = storeToRefs(useAuthStore());

  const setDefaultFilters = async () => {
    const { personalizeForm } = usePersonalize();

    if (user && user.value?.userType !== 'relocated') {
      if (personalizeForm.value.city && personalizeForm.value.city !== '') {
        filters.value.city = personalizeForm.value.city;
        addOrClearFilter('city', true, personalizeForm.value.city);
      }
      if (personalizeForm.value.arrival && personalizeForm.value.arrival !== '') {
        filters.value.startAvailabilityDate = personalizeForm.value.arrival;
        addOrClearFilter('startAvailabilityDate', true, personalizeForm.value.arrival);
      }
      if (personalizeForm.value.departure && personalizeForm.value.departure !== '') {
        filters.value.endAvailabilityDate = personalizeForm.value.departure;
        addOrClearFilter('endAvailabilityDate', true, personalizeForm.value.departure);
      }
    } else if (user && user.value?.userType === 'relocated') {
      const { handleGetRelocatedGroup } = useRelocated();

      const organizationData = await handleGetRelocatedGroup();

      const location = organizationData.group.Project.ProjectLocation;
      const checkIn = dayjs(organizationData.group.checkInDate).format('YYYY-MM-DD');
      const checkOut = dayjs(organizationData.group.checkOutDate).format('YYYY-MM-DD');

      filters.value.city = location.city ?? null;
      filters.value.countryCode = location.countryCode ?? null;
      filters.value.startAvailabilityDate = checkIn;
      filters.value.endAvailabilityDate = checkOut;

      addOrClearFilter('city', true, location.city ?? null);
      addOrClearFilter('countryCode', true, location.countryCode ?? null);
      addOrClearFilter('startAvailabilityDate', true, checkIn);
      addOrClearFilter('endAvailabilityDate', true, checkOut);
    }
  };

  const clearFilters = async (loadDefaultFilters = false) => {
    filters.value = {
      minPrice: undefined,
      maxPrice: undefined,
      lon: undefined,
      lat: undefined,
      PropertyTypeId: -1,
      city: '',
      countryCode: '',
      country: '',
      startAvailabilityDate: '',
      endAvailabilityDate: '',
    };
    addOrClearFilter('minPrice', false, false);
    addOrClearFilter('maxPrice', false, false);
    addOrClearFilter('PropertyTypeId', false, false);
    addOrClearFilter('city', false, false);
    addOrClearFilter('startAvailabilityDate', false, false);
    addOrClearFilter('endAvailabilityDate', false, false);
    addOrClearFilter('countryCode', false, false);
    addOrClearFilter('lat', false, false);
    addOrClearFilter('lon', false, false);

    if (loadDefaultFilters) {
      setDefaultFilters();
    }
  };

  const applyFilters = async () => {
    addOrClearFilter(
      'minPrice',
      !!filters.value.minPrice,
      filters.value.minPrice ? Number(filters.value.minPrice) : false,
    );
    addOrClearFilter(
      'maxPrice',
      !!filters.value.maxPrice,
      filters.value.maxPrice ? Number(filters.value.maxPrice) : false,
    );
    addOrClearFilter(
      'PropertyTypeId',
      filters.value.PropertyTypeId !== -1,
      filters.value.PropertyTypeId,
    );
    addOrClearFilter('countryCode', filters.value.countryCode !== '', filters.value.countryCode);
    addOrClearFilter('city', filters.value.city !== '', filters.value.city);
    addOrClearFilter(
      'startAvailabilityDate',
      filters.value.startAvailabilityDate !== '',
      filters.value.startAvailabilityDate,
    );
    addOrClearFilter(
      'endAvailabilityDate',
      filters.value.endAvailabilityDate !== '',
      filters.value.endAvailabilityDate,
    );
    addOrClearFilter(
      'lat',
      !!filters.value.lat,
      filters.value.lat ? Number(filters.value.lat) : false,
    );
    addOrClearFilter(
      'lon',
      !!filters.value.lon,
      filters.value.lon ? Number(filters.value.lon) : false,
    );

    resetStore();

    handleGetProperties(1);
  };

  return { applyFilters, clearFilters, setDefaultFilters };
};
